import { Accordion, Group, Title } from "@mantine/core";
import { IconGraph, IconInfoCircle } from "@tabler/icons-react";
import { DateTime } from "luxon";
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef
} from "mantine-react-table-open";
import { useMemo } from "react";
import { Link, generatePath, useParams } from "react-router";
import { getDefaultMRTOptions } from "../../../../../mantine/getDefaultMRTOptions";
import { ROUTES } from "../../../../../routes";
import urls, { OptiSupportEndpoints } from "../../../../../urls";
import { AnchorLink } from "../../../../AnchorLink/AnchorLink";
import { TitleWithStatusSymbol } from "../../../../BuildingBlocks/Layout/TitleWithStatusSymbol/TitleWithStatusSymbol";
import { NoDataMessage } from "../../../../BuildingBlocks/NoDataMessage/NoDataMessage";
import { IconHelpText } from "../../../../IconHelpText/IconHelpText";
import {
  StatusSymbol,
  StatusSymbolColor
} from "../../../../Icons/StatusSymbol";
import { LoadOrError } from "../../../../LoadOrError/LoadOrError";
import { ObservationPeriodCell } from "../../../../Todos/TodoTable/Cells/ObservationPeriodCell";
import { TABS } from "../../EnergyDataView";
import { useFetchDataSufficiencyAssessment } from "../../hooks/useFetchDataSufficiencyAssessment";
import { METER_ID_PREFIX } from "../../utils/getMeterIdFromSelectedId";
import { NoScada } from "../NoScada/NoScada";
import "./AvailabilityWidget.scss";

const SUFFICIENT_TEXT = "ausreichend";
const NOT_SUFFICIENT_TEXT = "nicht ausreichend";

export enum DataSufficencyResponseType {
  WindDataSufficiencyResponse = "WindDataSufficiencyResponse",
  MeterBasedDataSufficiencyResponse = "MeterBasedDataSufficiencyResponse",
  NoDataSufficiencyResponse = "NoDataSufficiencyResponse"
}

export interface WindDataSufficiencyResponse {
  dataIsSufficient: boolean;
  generators: Array<GeneratorDataSufficiency>;
  gridMeter: GridMeterData;
  siteName: string;
  siteResult: SiteResultData;
  thresholds: ThresholdData;
  year: number;
  kind: DataSufficencyResponseType.WindDataSufficiencyResponse;
}

export interface MeterBasedDataSufficiencyResponse {
  dataIsSufficient: boolean;
  siteName: string;
  siteId: number;
  configurationComplete: boolean;
  meters: Array<MeterData>;
  year: number;
  kind: DataSufficencyResponseType.MeterBasedDataSufficiencyResponse;
}

export interface NoDataSufficiencyResponse {
  reason: string;
  message: string;
  kind: DataSufficencyResponseType.NoDataSufficiencyResponse;
}

interface GeneratorDataSufficiency {
  percentageOfExistingGenerationFeedinData: number;
  percentageOfExistingConsumptionFeedoutData: number;
  dataIsSufficient: boolean;
  isInRelevantGroup: boolean;
  metaData: {
    name: string;
    person: string;
  };
}

interface MeterData {
  percentageOfExistingGenerationFeedinData: number;
  percentageOfExistingConsumptionFeedoutData: number;
  dataIsSufficient: boolean;
  metaData: {
    id: number;
    name: string;
    number: string | null;
    locationFeedin: string | null;
    locationFeedout: string | null;
  };
  observationPeriod: [string, string];
}

type GridMeterData = Omit<MeterData, "observationPeriod">;

interface SiteResultData {
  allRootMetersHaveCompleteData: boolean;
  relevantGroupsHaveCompleteConsumptionData: boolean;
  relevantGroupsHaveCompleteGenerationData: boolean;
  allWeaHaveCompleteGenerationData: boolean;
}

interface ThresholdData {
  necessaryShareOfExistingGridMeterData: number;
  necessaryShareOfExistingGeneratorData: number;
}

interface AvailabilityWidgetProps {
  siteName: string;
  siteId: number;
  projectId: string;
  year: number;
}

function AvailabilityWidget({
  siteId,
  siteName,
  projectId,
  year
}: AvailabilityWidgetProps) {
  const { isLoading, error, data } = useFetchDataSufficiencyAssessment(
    siteId,
    year
  );

  function renderResponse() {
    if (data) {
      if (
        data.kind === DataSufficencyResponseType.WindDataSufficiencyResponse
      ) {
        return (
          <WindAvailabilityWidget data={data} siteId={siteId} year={year} />
        );
      } else if (
        data.kind ===
        DataSufficencyResponseType.MeterBasedDataSufficiencyResponse
      ) {
        return (
          <MeterBasedAvailabilityWidget
            data={data}
            projectId={projectId}
            year={year}
          />
        );
      } else {
        return <p>{data.message}</p>;
      }
    } else {
      return <NoDataMessage message="Es konnten keine Daten geladen werden" />;
    }
  }

  const showStatusSymbolInTitle =
    data?.kind === DataSufficencyResponseType.WindDataSufficiencyResponse ||
    data?.kind === DataSufficencyResponseType.MeterBasedDataSufficiencyResponse;

  return (
    <Accordion.Item className="AvailabilityWidget" value={siteId.toString()}>
      <Accordion.Control>
        <TitleWithStatusSymbol
          isGreen={showStatusSymbolInTitle && data?.dataIsSufficient}
          isLoading={isLoading}
          isRed={showStatusSymbolInTitle && !data?.dataIsSufficient}
          title={siteName}
        />
      </Accordion.Control>
      <Accordion.Panel>
        <LoadOrError error={error} loading={isLoading}>
          {renderResponse()}
        </LoadOrError>
      </Accordion.Panel>
    </Accordion.Item>
  );
}

interface WindAvailabilityWidgetProps {
  data: WindDataSufficiencyResponse;
  year: number;
  siteId: number;
}

function WindAvailabilityWidget({
  data,
  year,
  siteId
}: WindAvailabilityWidgetProps) {
  return (
    <>
      <Title order={6}>Verfügbarkeit der Daten am Netzverknüpfungspunkt</Title>
      <p>
        Für die Abrechnung werden die Daten des Messstellenbetreibers am
        Netzverknüpfungspunkt mit einer Verfügbarkeit ab{" "}
        {data.thresholds.necessaryShareOfExistingGridMeterData}% für Einspeisung
        UND Entnahme benötigt.
      </p>
      <NvpTable gridMeterData={data.gridMeter} year={year} />
      <Title order={6}>Verfügbarkeit der Anlagendaten</Title>
      <p>
        Bitte stellen Sie für folgende Anlagen SCADA-Daten mit einer
        Verfügbarkeit von mindestens{" "}
        {data.thresholds.necessaryShareOfExistingGeneratorData}% bereit.
        Alternativ kann bei Drittanlagen im Park über Gruppenzähler eine
        ausreichende Datenverfügbarkeit erreicht werden. Weiterführende
        Informationen zu den Anforderungen an verfügbare Messdaten finden Sie in
        unserem{" "}
        <AnchorLink
          href={urls.optiSupport(OptiSupportEndpoints.EnergiedatenAusreichend)}
        >
          Hilfeportal
        </AnchorLink>
        .
      </p>
      <NoScada siteId={siteId} />
      <GeneratorDataTable
        generatorDataSufficiencies={data.generators}
        necessaryShareOfExistingGeneratorData={
          data.thresholds.necessaryShareOfExistingGeneratorData
        }
        year={year}
      />
    </>
  );
}

interface NonWindAvailabilityWidgetProps {
  data: MeterBasedDataSufficiencyResponse;
  projectId: string;
  year: number;
}

function MeterBasedAvailabilityWidget({
  data,
  projectId,
  year
}: NonWindAvailabilityWidgetProps) {
  function renderConfigurationDisclaimer() {
    if (!data.configurationComplete) {
      return (
        <p>
          Eine abschließende Bewertung der Daten ist erst möglich, wenn die
          Aufgabe &quot;Konfiguration prüfen&quot; abgeschlossen wurde
        </p>
      );
    }
  }

  return (
    <>
      {renderConfigurationDisclaimer()}
      <Title order={6}>Verfügbarkeit Marktkommunikationszähler</Title>
      <MeterTable
        meters={data.meters}
        projectId={projectId}
        showMarketColumn={true}
        year={year}
      ></MeterTable>
      <Title order={6}>Verfügbarkeit Unterzähler</Title>
      <MeterTable
        meters={data.meters}
        projectId={projectId}
        showMarketColumn={false}
        year={year}
      ></MeterTable>
    </>
  );
}

interface PercentageCellProps {
  percentage: number;
}

function PercentageCell({ percentage }: PercentageCellProps) {
  return <span>{percentage !== null ? percentage + "%" : "-"}</span>;
}

interface MeterTableProps {
  projectId: string;
  meters: Array<MeterData>;
  showMarketColumn: boolean;
  year: number;
}

function MeterTable({
  projectId,
  meters,
  showMarketColumn,
  year
}: MeterTableProps) {
  const yearDate = DateTime.fromISO(`${year}-01-01`);

  const yearStart = yearDate.toISODate();
  const yearEnd = yearDate.endOf("year").toISODate();

  const columns: Array<MRT_ColumnDef<MeterData>> = useMemo(
    () => [
      {
        header: "Name",
        accessorKey: "metaData.name",
        Cell: ({ row }) => (
          <Link
            to={{
              pathname: generatePath(
                `${ROUTES.energyData.entry}/${TABS.TAB_RAW_ENERGY_DATA}`,
                { projectId: projectId ?? null }
              ),
              search: `?firstDate=${yearStart}&lastDate=${yearEnd}&selectedId=${METER_ID_PREFIX}${row.original.metaData.id}`
            }}
          >
            {row.original.metaData.name}
          </Link>
        )
      },
      showMarketColumn
        ? {
            header: "MeLo - MaLo",
            accessorFn: (rowData) =>
              getMeteringOrMarketLocation(
                rowData.metaData.locationFeedin,
                rowData.metaData.locationFeedout
              )
          }
        : {
            header: "Zählernummer",
            accessorKey: "metaData.number"
          },
      {
        header: "Einspeisung",
        accessorKey: "percentageOfExistingGenerationFeedinData",
        size: 150,
        Cell: ({ row }) => (
          <PercentageCell
            percentage={row.original.percentageOfExistingGenerationFeedinData}
          />
        )
      },
      {
        header: "Entnahme",
        accessorKey: "percentageOfExistingConsumptionFeedoutData",
        size: 150,
        Cell: ({ row }) => (
          <PercentageCell
            percentage={row.original.percentageOfExistingConsumptionFeedoutData}
          />
        )
      },
      {
        header: `Ausreichend für Meldungen ${year}?`,
        accessorKey: "dataIsSufficient",
        minSize: 270,
        Cell: ({ row }) => (
          <SufficiencyCell sufficient={row.original.dataIsSufficient} />
        )
      },
      {
        header: `Betrachtungszeitraum`,
        accessorKey: "observationPeriod",
        minSize: 150,
        Cell: ({ row }) => (
          <ObservationPeriodCell
            period={[
              DateTime.fromISO(row.original.observationPeriod[0]),
              DateTime.fromISO(row.original.observationPeriod[1])
            ]}
          />
        )
      }
    ],
    [projectId, showMarketColumn, yearStart, yearEnd, year]
  );

  const tableData = useMemo(
    () =>
      meters.filter(
        (meter) =>
          !!getMeteringOrMarketLocation(
            meter.metaData.locationFeedin,
            meter.metaData.locationFeedout
          ) === showMarketColumn
      ),
    [meters, showMarketColumn]
  );

  const mrtTable = useMantineReactTable<MeterData>({
    ...getDefaultMRTOptions({
      className: "MeterTable",
      emptyRowsFallbackText: "Keine Zähler vorhanden.",
      hideBorder: true
    }),
    enablePagination: false,
    enableToolbarInternalActions: false,
    columns: columns,
    data: tableData
  });

  return <MantineReactTable table={mrtTable} />;
}

function getMeteringOrMarketLocation(
  locationFeedin: string | null,
  locationFeedout: string | null
) {
  return [locationFeedin, locationFeedout].filter(Boolean).join(", ");
}

interface NvpTableProps {
  gridMeterData: GridMeterData;
  year: number;
}

function NvpTable({ gridMeterData, year }: NvpTableProps) {
  const columns: Array<MRT_ColumnDef<GridMeterData>> = [
    { header: "Netzverknüpfungspunkt", accessorFn: () => "NVP" },
    {
      header: "Zähler",
      accessorKey: "metaData.name",
      Cell: ({ row }) => {
        const { id, name } = row.original.metaData;
        return <MeterCell id={id} name={name} year={year} />;
      }
    },
    {
      header: "MeLo - MaLo",
      accessorFn: (rowData) =>
        getMeteringOrMarketLocation(
          rowData.metaData.locationFeedin,
          rowData.metaData.locationFeedout
        )
    },
    {
      header: "Einspeisung",
      accessorKey: "percentageOfExistingGenerationFeedinData",
      size: 150,
      Cell: ({ row }) => (
        <PercentageCell
          percentage={row.original.percentageOfExistingGenerationFeedinData}
        />
      )
    },
    {
      header: "Entnahme",
      accessorKey: "percentageOfExistingConsumptionFeedoutData",
      size: 150,
      Cell: ({ row }) => (
        <PercentageCell
          percentage={row.original.percentageOfExistingConsumptionFeedoutData}
        />
      )
    },
    {
      header: `Ausreichend für Meldungen ${year}?`,
      accessorKey: "dataIsSufficient",
      size: 270,
      Cell: ({ row }) => (
        <SufficiencyCell sufficient={row.original.dataIsSufficient} />
      )
    }
  ];

  const mrtTable = useMantineReactTable<GridMeterData>({
    ...getDefaultMRTOptions({ className: "NvpTable", hideBorder: true }),
    enableTopToolbar: false,
    enablePagination: false,
    columns: columns,
    data: [gridMeterData]
  });

  return <MantineReactTable table={mrtTable} />;
}

interface GeneratorDataTableProps {
  generatorDataSufficiencies: Array<GeneratorDataSufficiency>;
  year: number;
  necessaryShareOfExistingGeneratorData: number;
}

function GeneratorDataTable({
  generatorDataSufficiencies,
  year,
  necessaryShareOfExistingGeneratorData
}: GeneratorDataTableProps) {
  const columns: Array<MRT_ColumnDef<GeneratorDataSufficiency>> = [
    { header: "Erzeugungsanlage", accessorKey: "metaData.name" },
    { header: "Unternehmen", accessorKey: "metaData.person" },
    {
      header: "Erzeugung",
      accessorKey: "percentageOfExistingGenerationFeedinData",
      size: 150,
      Cell: ({ row }) => (
        <PercentageCell
          percentage={row.original.percentageOfExistingGenerationFeedinData}
        />
      )
    },
    {
      header: "Verbrauch",
      accessorKey: "percentageOfExistingConsumptionFeedoutData",
      size: 150,
      Cell: ({ row }) => (
        <PercentageCell
          percentage={row.original.percentageOfExistingConsumptionFeedoutData}
        />
      )
    },
    {
      header: `Ausreichend für Meldungen ${year}?`,
      accessorKey: "dataIsSufficient",
      size: 270,
      Cell: ({ row }) => (
        <SufficiencyCell
          dataMeetsThreshold={
            row.original.percentageOfExistingGenerationFeedinData >=
              necessaryShareOfExistingGeneratorData &&
            row.original.percentageOfExistingConsumptionFeedoutData >=
              necessaryShareOfExistingGeneratorData
          }
          showThresholdInfo
          sufficient={row.original.dataIsSufficient}
        />
      )
    }
  ];

  const mrtTable = useMantineReactTable<GeneratorDataSufficiency>({
    ...getDefaultMRTOptions({
      className: "GeneratorDataTable",
      emptyRowsFallbackText: "Kein Anlagenzähler vorhanden.",
      hideBorder: true
    }),
    enableTopToolbar: false,
    enablePagination: false,
    columns: columns,
    data: generatorDataSufficiencies
  });

  return <MantineReactTable table={mrtTable} />;
}

interface SufficiencyCellProps {
  sufficient: boolean;
  dataMeetsThreshold?: boolean;
  showThresholdInfo?: boolean;
}

function SufficiencyCell({
  sufficient,
  dataMeetsThreshold,
  showThresholdInfo
}: SufficiencyCellProps) {
  const sufficiencyText = sufficient ? SUFFICIENT_TEXT : NOT_SUFFICIENT_TEXT;

  return (
    <span>
      <StatusSymbol
        color={sufficient ? StatusSymbolColor.Green : StatusSymbolColor.Red}
      />{" "}
      {sufficiencyText}{" "}
      {showThresholdInfo && sufficient && !dataMeetsThreshold && (
        <IconHelpText
          aria-label="Information zur Datenverfügbarkeit"
          helpText="Die Datenverfügbarkeit ist für diese Anlage ausreichend, obwohl die Anlage selbst nicht über ausreichende Energiedaten verfügt. Die fehlenden Werte werden über die Energiedaten des Netzverknüpfungspunkts oder über geeichte Unterzähler abgeschätzt."
          Icon={IconInfoCircle}
        />
      )}
    </span>
  );
}

interface MeterCellProps {
  id: number;
  name: string;
  year: number;
}

function MeterCell({ id, name, year }: MeterCellProps) {
  const { projectId } = useParams();
  const yearDate = DateTime.fromISO(`${year}-01-01`);

  const yearStart = yearDate.toISODate();
  const yearEnd = yearDate.endOf("year").toISODate();

  return (
    <Group justify="space-between" wrap="nowrap">
      <span className="MeterName">{name}</span>
      {projectId && (
        <Link
          to={{
            pathname: generatePath(
              `${ROUTES.energyData.entry}/${TABS.TAB_RAW_ENERGY_DATA}`,
              { projectId }
            ),
            search: `?firstDate=${yearStart}&lastDate=${yearEnd}&selectedId=${METER_ID_PREFIX}${id}`
          }}
        >
          <IconGraph size="1.3rem" />
        </Link>
      )}
    </Group>
  );
}

export {
  AvailabilityWidget,
  AvailabilityWidgetProps,
  MeterCell,
  PercentageCell,
  SufficiencyCell
};
